import { ZodIssueCode, array, boolean, discriminatedUnion, enum as enumFactory, literal, number, strictObject, string, union } from "../../lazy-zod.js";
import { spaceMentionLabelSchema } from "../http/space-mention-label.js";
import { spacePasswordSchema } from "../http/space-password.js";
import { executionContextAssignmentSchema, executionContextRequestSchema, executionContextSelectionSchema, executionContextTaskSchema } from "../protocol/execution-context.js";
import { messageEnvelopeSchema } from "../protocol/message-envelope.js";
import { spaceContractSchema } from "../space-mesh/space-contract.js";
import { spaceThreadContextSchema } from "../space-mesh/space-thread.js";
import { runtimeAgentControllerConversationIdMappingSpecSchema, runtimeDispatchPolicySchema, runtimeTransportModeSchema } from "./controller-contract.js";
import { spaceMessageFileReferenceSchema, spaceMessageFileReferencesSchema } from "../http/space-files.js";
import { spaceWorkHandoffSchema } from "../../space-work-handoff.js";
import { centerAgentInteractionSourceSchema } from "../protocol/center-agent-interaction-source.js";
import { spaceMessageAttachmentsSchema } from "../protocol/space-attachment.js";
import { wakeDeviceWaitSchema } from "../protocol/wake-device-wait.js";
import { signalTextReplyToSchema } from "../protocol/signal.js";
import { spaceCallerContextSchema } from "../protocol/space-caller-context.js";
import { agentTaskCompletionObservationSchema, agentTaskHandleSchema } from "../runtime/agent-task-coordination.js";
import { openMeldDaemonMentionDispatchRuleSourceSchema } from "./mention-dispatch-result.js";
//#region src/schemas/daemon/dispatch-queue.ts
const OPENMELD_DAEMON_DISPATCH_QUEUE_SCHEMA_VERSION = 2;
const REMOTE_AGENT_NATIVE_DELIVERY_DEADLINE_PROBE_QUEUE_SCHEMA_VERSION = 3;
const AGENT_TASK_CONTINUATION_QUEUE_SCHEMA_VERSION = 4;
const AGENT_TASK_OBSERVATION_REDRIVE_QUEUE_SCHEMA_VERSION = 5;
const OPENMELD_DAEMON_DISPATCH_CONTRACT_VERSION = 2;
const openMeldDaemonDispatchDeliveryRecoveryReasonSchema = enumFactory(["connection_replaced", "route_lost"]);
const openMeldDaemonDispatchQueueRecoveryReasonSchema = union([
	openMeldDaemonDispatchDeliveryRecoveryReasonSchema,
	literal("agent_profile_work_coordinator_promoted"),
	literal("hosted_admission_incomplete")
]);
const openMeldDaemonDispatchRulesSnapshotSchema = strictObject({
	dispatchPolicy: runtimeDispatchPolicySchema,
	ruleSource: openMeldDaemonMentionDispatchRuleSourceSchema,
	ruleId: string().min(1),
	ruleReason: string().min(1)
});
const openMeldDaemonDispatchControllerSnapshotSchema = strictObject({
	bindingId: string().trim().min(1).nullable().optional(),
	configurationRevision: number().int().positive().optional(),
	targetBindingState: enumFactory(["bound", "missing"]).describe("Compatibility seed evidence captured at Space handoff time. RuntimeCoordinator head lifecycle and Core Binding lookup remain the Wake outcome authorities."),
	targetBindingEnabled: boolean().describe("Compatibility seed evidence captured at Space handoff time. RuntimeCoordinator head lifecycle and Core Binding lookup remain the Wake outcome authorities."),
	agentControllerRef: string().min(1).nullable()
});
const openMeldDaemonAcceptedDispatchSnapshotSchema = strictObject({
	contextRequest: executionContextRequestSchema.optional(),
	executionContext: executionContextAssignmentSchema.optional(),
	deviceWait: wakeDeviceWaitSchema.optional(),
	controllerSnapshot: strictObject({
		bindingId: string().trim().min(1),
		configurationRevision: number().int().positive(),
		targetBindingState: literal("bound"),
		targetBindingEnabled: literal(true),
		agentControllerRef: string().trim().min(1)
	}),
	openMeldServiceRuntimeId: string().trim().min(1),
	payloadFingerprint: string().regex(/^sha256:[a-f0-9]{64}$/),
	runtimeKind: literal("openmeld_service")
});
const openMeldDaemonDispatchSnapshotSchema = strictObject({
	dispatchPolicy: runtimeDispatchPolicySchema,
	ruleSource: openMeldDaemonMentionDispatchRuleSourceSchema,
	ruleId: string().min(1),
	ruleReason: string().min(1),
	targetBindingState: enumFactory(["bound", "missing"]).describe("Compatibility seed evidence captured at Space handoff time. Do not use this snapshot as Binding authority or per-Wake outcome truth."),
	targetBindingEnabled: boolean().describe("Compatibility seed evidence captured at Space handoff time. Do not use this snapshot as Binding authority or per-Wake outcome truth.")
});
const openMeldDaemonDispatchSourceSignalSnapshotSchema = strictObject({
	workHandoff: spaceWorkHandoffSchema.optional(),
	contextMessages: array(strictObject({
		signalId: string().min(1),
		author: string().min(1).nullable(),
		text: string().max(16e3),
		files: spaceMessageFileReferencesSchema.optional(),
		attachments: spaceMessageAttachmentsSchema.optional()
	})).max(3).optional(),
	callerContext: spaceCallerContextSchema.optional(),
	pinnedFiles: array(spaceMessageFileReferenceSchema).max(100).optional(),
	quotedMessage: strictObject({
		signalId: string().min(1),
		author: string().min(1).nullable(),
		text: string().max(16e3).nullable(),
		status: enumFactory([
			"available",
			"recalled",
			"unavailable"
		])
	}).optional(),
	text: string().min(1),
	profileId: string().min(1).nullable(),
	profileKind: enumFactory([
		"agent",
		"human",
		"system"
	]).nullable(),
	profileMentionLabel: spaceMentionLabelSchema.optional(),
	attachments: spaceMessageAttachmentsSchema.optional(),
	files: spaceMessageFileReferencesSchema.optional(),
	centerAgentInteractionSource: centerAgentInteractionSourceSchema.optional()
});
const openMeldDaemonDispatchReplyToSnapshotSchema = signalTextReplyToSchema;
const openMeldDaemonDispatchContinuitySnapshotSchema = strictObject({
	messagesSinceThen: number().int().finite().min(0),
	higherAuthorityUpdates: boolean()
});
const openMeldDaemonDispatchOwnerSchema = strictObject({
	kind: literal("owner_account"),
	id: string().min(1)
});
const openMeldDaemonDispatchQueueLegacyMessageSchemaV1 = strictObject({
	v: literal(1),
	dispatchId: string().min(1),
	spaceId: string().min(1),
	sourceEventId: string().min(1),
	targetProfileId: string().min(1),
	idempotencyKey: string().min(1),
	enqueuedAtMs: number().int().nonnegative()
});
const openMeldDaemonDispatchQueueMessageSchema = strictObject({
	v: literal(2),
	dispatchId: string().min(1),
	contextSelection: executionContextSelectionSchema.optional(),
	executionContext: executionContextTaskSchema.optional(),
	deviceWait: wakeDeviceWaitSchema.optional(),
	spaceId: string().min(1),
	organizationId: string().trim().min(1).describe("Canonical Space Organization snapshot captured by the Space Durable Object when it creates the dispatch outbox seed. Optional only while old producers remain in the phase-1 rollout window.").optional(),
	sourceEventId: string().min(1),
	sourceClientMessageId: string().min(1).max(200).nullable().optional(),
	dispatchOwner: openMeldDaemonDispatchOwnerSchema.optional(),
	targetProfileId: string().min(1),
	targetProfileMentionLabel: spaceMentionLabelSchema.optional(),
	dispatchChainId: string().min(1),
	autoRelayDepth: number().int().nonnegative(),
	visitedProfileIds: array(string().min(1)).max(256).default([]),
	visitedProfileIdsHash: string().min(1),
	dispatchContractVersion: number().int().positive(),
	dispatchPolicy: runtimeDispatchPolicySchema,
	ruleSource: openMeldDaemonMentionDispatchRuleSourceSchema,
	ruleId: string().min(1),
	ruleReason: string().min(1),
	taskId: string().min(1),
	transportMode: runtimeTransportModeSchema.nullable(),
	spacePassword: spacePasswordSchema.optional(),
	controllerSnapshot: openMeldDaemonDispatchControllerSnapshotSchema,
	contextIdMappingSpec: runtimeAgentControllerConversationIdMappingSpecSchema.nullable(),
	dispatchRulesSnapshot: openMeldDaemonDispatchRulesSnapshotSchema,
	dispatchSnapshot: openMeldDaemonDispatchSnapshotSchema,
	spaceContractSnapshot: spaceContractSchema.optional(),
	continuitySnapshot: openMeldDaemonDispatchContinuitySnapshotSchema.optional(),
	sourceSignalSnapshot: openMeldDaemonDispatchSourceSignalSnapshotSchema.optional(),
	sourceMessageEnvelope: messageEnvelopeSchema.optional(),
	replyToSnapshot: openMeldDaemonDispatchReplyToSnapshotSchema.optional(),
	threadContext: spaceThreadContextSchema.optional(),
	spaceNameSnapshot: string().min(1).nullable().optional(),
	sourceProfileNameSnapshot: string().min(1).nullable().optional(),
	targetProfileNameSnapshot: string().min(1).nullable().optional(),
	deliveryRecoveryReason: openMeldDaemonDispatchQueueRecoveryReasonSchema.optional(),
	idempotencyKey: string().min(1),
	enqueuedAtMs: number().int().nonnegative()
}).superRefine((value, context) => {
	if (value.dispatchSnapshot.dispatchPolicy !== value.dispatchPolicy) context.addIssue({
		code: ZodIssueCode.custom,
		path: ["dispatchSnapshot", "dispatchPolicy"],
		message: "dispatchSnapshot.dispatchPolicy must match dispatchPolicy"
	});
	if (value.dispatchSnapshot.ruleSource !== value.ruleSource) context.addIssue({
		code: ZodIssueCode.custom,
		path: ["dispatchSnapshot", "ruleSource"],
		message: "dispatchSnapshot.ruleSource must match ruleSource"
	});
	if (value.dispatchSnapshot.ruleId !== value.ruleId) context.addIssue({
		code: ZodIssueCode.custom,
		path: ["dispatchSnapshot", "ruleId"],
		message: "dispatchSnapshot.ruleId must match ruleId"
	});
	if (value.dispatchSnapshot.ruleReason !== value.ruleReason) context.addIssue({
		code: ZodIssueCode.custom,
		path: ["dispatchSnapshot", "ruleReason"],
		message: "dispatchSnapshot.ruleReason must match ruleReason"
	});
	if (value.dispatchRulesSnapshot.dispatchPolicy !== value.dispatchPolicy || value.dispatchRulesSnapshot.ruleSource !== value.ruleSource || value.dispatchRulesSnapshot.ruleId !== value.ruleId || value.dispatchRulesSnapshot.ruleReason !== value.ruleReason) context.addIssue({
		code: ZodIssueCode.custom,
		path: ["dispatchRulesSnapshot"],
		message: "dispatchRulesSnapshot must match top-level dispatch rule fields"
	});
	if (value.controllerSnapshot.targetBindingState !== value.dispatchSnapshot.targetBindingState || value.controllerSnapshot.targetBindingEnabled !== value.dispatchSnapshot.targetBindingEnabled) context.addIssue({
		code: ZodIssueCode.custom,
		path: ["controllerSnapshot"],
		message: "controllerSnapshot target binding fields must match dispatchSnapshot"
	});
});
const remoteAgentNativeDeliveryDeadlineProbeQueueMessageSchema = strictObject({
	v: literal(3),
	kind: literal("remote_agent_native_delivery_deadline_probe"),
	submissionId: string().min(1),
	idempotencyKey: string().min(1),
	organizationId: string().min(1),
	spaceId: string().min(1),
	dispatchId: string().min(1),
	enqueuedAtMs: number().int().nonnegative(),
	deadlineAtMs: number().int().nonnegative()
});
const agentTaskContinuationQueueMessageSchema = strictObject({
	enqueuedAtMs: number().int().nonnegative(),
	idempotencyKey: string().trim().min(1).max(512),
	kind: literal("agent_task_continuation"),
	subscriptionId: string().trim().min(1).max(512),
	v: literal(4)
});
const agentTaskObservationRedriveQueueMessageSchema = strictObject({
	deliveryId: string().trim().min(1).max(1024),
	enqueuedAtMs: number().int().nonnegative(),
	idempotencyKey: string().trim().min(1).max(1536),
	kind: literal("agent_task_observation_redrive"),
	observation: agentTaskCompletionObservationSchema,
	ownerUserId: string().trim().min(1).max(512),
	runtimeCoordinatorExecutionId: string().trim().min(1).max(512),
	task: agentTaskHandleSchema,
	v: literal(5)
});
const openMeldDaemonDispatchQueueIngressSchema = discriminatedUnion("v", [
	openMeldDaemonDispatchQueueLegacyMessageSchemaV1,
	openMeldDaemonDispatchQueueMessageSchema,
	remoteAgentNativeDeliveryDeadlineProbeQueueMessageSchema,
	agentTaskContinuationQueueMessageSchema,
	agentTaskObservationRedriveQueueMessageSchema
]);
const resolveReceivedQueueSchemaVersion = (value) => {
	if (!(value && typeof value === "object") || Array.isArray(value)) return null;
	const version = value.v;
	return typeof version === "number" && Number.isInteger(version) ? version : null;
};
const buildLegacyQueueIssues = () => [{
	code: ZodIssueCode.custom,
	message: "legacy dispatch queue schema version unsupported",
	path: ["v"]
}];
const parseCurrentDispatchQueueMessage = (value) => {
	const parsed = openMeldDaemonDispatchQueueMessageSchema.safeParse(value);
	if (parsed.success) return {
		kind: "current_v2",
		message: parsed.data,
		receivedQueueSchemaVersion: parsed.data.v
	};
	return {
		kind: "invalid",
		issues: parsed.error.issues,
		receivedQueueSchemaVersion: 2
	};
};
const parseRemoteAgentDeadlineProbeQueueMessage = (value) => {
	const parsed = remoteAgentNativeDeliveryDeadlineProbeQueueMessageSchema.safeParse(value);
	if (parsed.success) return {
		kind: "remote_agent_deadline_probe",
		message: parsed.data,
		receivedQueueSchemaVersion: parsed.data.v
	};
	return {
		kind: "invalid",
		issues: parsed.error.issues,
		receivedQueueSchemaVersion: 3
	};
};
const parseAgentTaskContinuationQueueMessage = (value) => {
	const parsed = agentTaskContinuationQueueMessageSchema.safeParse(value);
	if (parsed.success) return {
		kind: "agent_task_continuation",
		message: parsed.data,
		receivedQueueSchemaVersion: parsed.data.v
	};
	return {
		kind: "invalid",
		issues: parsed.error.issues,
		receivedQueueSchemaVersion: 4
	};
};
const parseAgentTaskObservationRedriveQueueMessage = (value) => {
	const parsed = agentTaskObservationRedriveQueueMessageSchema.safeParse(value);
	if (parsed.success) return {
		kind: "agent_task_observation_redrive",
		message: parsed.data,
		receivedQueueSchemaVersion: parsed.data.v
	};
	return {
		kind: "invalid",
		issues: parsed.error.issues,
		receivedQueueSchemaVersion: 5
	};
};
const parseLegacyDispatchQueueMessage = (value) => {
	const parsed = openMeldDaemonDispatchQueueLegacyMessageSchemaV1.safeParse(value);
	if (parsed.success) return {
		kind: "legacy_v1",
		message: parsed.data,
		issues: buildLegacyQueueIssues(),
		receivedQueueSchemaVersion: parsed.data.v
	};
	return {
		kind: "invalid",
		issues: parsed.error.issues,
		receivedQueueSchemaVersion: 1
	};
};
const parseOpenMeldDaemonDispatchQueueIngress = (value) => {
	const receivedQueueSchemaVersion = resolveReceivedQueueSchemaVersion(value);
	if (receivedQueueSchemaVersion === 2) return parseCurrentDispatchQueueMessage(value);
	if (receivedQueueSchemaVersion === 3) return parseRemoteAgentDeadlineProbeQueueMessage(value);
	if (receivedQueueSchemaVersion === 4) return parseAgentTaskContinuationQueueMessage(value);
	if (receivedQueueSchemaVersion === 5) return parseAgentTaskObservationRedriveQueueMessage(value);
	if (receivedQueueSchemaVersion === 1) return parseLegacyDispatchQueueMessage(value);
	const parsed = openMeldDaemonDispatchQueueIngressSchema.safeParse(value);
	if (parsed.success) {
		if (parsed.data.v === 2) return {
			kind: "current_v2",
			message: parsed.data,
			receivedQueueSchemaVersion: parsed.data.v
		};
		if (parsed.data.v === 3) return {
			kind: "remote_agent_deadline_probe",
			message: parsed.data,
			receivedQueueSchemaVersion: parsed.data.v
		};
		if (parsed.data.v === 4) return {
			kind: "agent_task_continuation",
			message: parsed.data,
			receivedQueueSchemaVersion: parsed.data.v
		};
		if (parsed.data.v === 5) return {
			kind: "agent_task_observation_redrive",
			message: parsed.data,
			receivedQueueSchemaVersion: parsed.data.v
		};
		return {
			kind: "legacy_v1",
			message: parsed.data,
			issues: buildLegacyQueueIssues(),
			receivedQueueSchemaVersion: parsed.data.v
		};
	}
	return {
		kind: "invalid",
		issues: parsed.error.issues,
		receivedQueueSchemaVersion
	};
};
//#endregion
export { AGENT_TASK_CONTINUATION_QUEUE_SCHEMA_VERSION, AGENT_TASK_OBSERVATION_REDRIVE_QUEUE_SCHEMA_VERSION, OPENMELD_DAEMON_DISPATCH_CONTRACT_VERSION, OPENMELD_DAEMON_DISPATCH_QUEUE_SCHEMA_VERSION, REMOTE_AGENT_NATIVE_DELIVERY_DEADLINE_PROBE_QUEUE_SCHEMA_VERSION, agentTaskContinuationQueueMessageSchema, agentTaskObservationRedriveQueueMessageSchema, openMeldDaemonAcceptedDispatchSnapshotSchema, openMeldDaemonDispatchContinuitySnapshotSchema, openMeldDaemonDispatchControllerSnapshotSchema, openMeldDaemonDispatchDeliveryRecoveryReasonSchema, openMeldDaemonDispatchOwnerSchema, openMeldDaemonDispatchQueueIngressSchema, openMeldDaemonDispatchQueueLegacyMessageSchemaV1, openMeldDaemonDispatchQueueMessageSchema, openMeldDaemonDispatchQueueRecoveryReasonSchema, openMeldDaemonDispatchReplyToSnapshotSchema, openMeldDaemonDispatchRulesSnapshotSchema, openMeldDaemonDispatchSnapshotSchema, openMeldDaemonDispatchSourceSignalSnapshotSchema, parseOpenMeldDaemonDispatchQueueIngress, remoteAgentNativeDeliveryDeadlineProbeQueueMessageSchema };

//# sourceMappingURL=dispatch-queue.js.map